// src/components/DittePresentiGiornale.js
// ═══════════════════════════════════════════════════════════════════════════
// GIORNALE DI CANTIERE — DITTE ESTERNE PRESENTI (ottobre 2026, W413)
// ═══════════════════════════════════════════════════════════════════════════
// Al posto del testo libero «Subappaltatori presenti»: una riga per ditta, scelta tra le
// imprese della commessa e l'anagrafica Ditte esterne, con quanti uomini c'erano e cosa
// hanno fatto. Da qui nascono gli uomini-giorno REALI di ogni ditta (utils/ditteEsterne.js).
// Chi non può leggere l'anagrafica sceglie tra le imprese della commessa o scrive il nome.
// Con il piano Gold, accanto alla ditta compare se ha documenti scaduti o mancanti.
// Il vecchio testo, nei giorni già scritti, resta visibile e modificabile.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { nomeNorm } from "../utils/ditteEsterne";
import { BadgeIdoneita } from "./IdoneitaDitte";

const nuovaRiga = () => ({ id: "dp_" + Math.random().toString(36).slice(2, 9), subappaltatoreId: "", nome: "", uomini: "", lavorazioni: "" });

export default function DittePresentiGiornale({ value = [], onChange, project, orgId, disabled = false, idoneita = null, testoVecchio = "", onTestoVecchio }) {
  const [anagrafica, setAnagrafica] = useState([]);
  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(query(collection(db, "subappaltatori"), where("organizationId", "==", orgId)),
      s => setAnagrafica(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(x => x.attivo !== false)), () => setAnagrafica([]));
  }, [orgId]);

  // prima le imprese di questa commessa, poi il resto dell'anagrafica
  const opzioni = useMemo(() => {
    const out = [];
    (project?.impreseEsterne || []).forEach(i => {
      const nome = i.subappaltatoreId ? (anagrafica.find(a => a.id === i.subappaltatoreId)?.ragioneSociale || i.nome) : i.nome;
      if (!String(nome || "").trim()) return;
      const v = i.subappaltatoreId ? `id:${i.subappaltatoreId}` : `n:${nome}`;
      if (!out.some(o => o.v === v)) out.push({ v, nome, subappaltatoreId: i.subappaltatoreId || "", gruppo: "commessa" });
    });
    anagrafica.slice().sort((a, b) => (a.ragioneSociale || "").localeCompare(b.ragioneSociale || "")).forEach(a => {
      const v = `id:${a.id}`;
      if (!out.some(o => o.v === v)) out.push({ v, nome: a.ragioneSociale, subappaltatoreId: a.id, gruppo: "anagrafica" });
    });
    return out;
  }, [project?.impreseEsterne, anagrafica]);

  const righe = value.length ? value : [];
  const aggiorna = (id, campi) => onChange(righe.map(r => (r.id === id ? { ...r, ...campi } : r)));
  const valoreDi = (r) => (r.subappaltatoreId ? `id:${r.subappaltatoreId}` : r.libero ? "__libero" : r.nome ? (opzioni.some(o => o.v === `n:${r.nome}`) ? `n:${r.nome}` : "__libero") : "");
  const totale = righe.reduce((t, r) => t + (Number(String(r.uomini).replace(",", ".")) || 0), 0);
  const inp = { width: "100%", padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div>
      {righe.map(r => {
        const val = valoreDi(r);
        const info = idoneita && r.subappaltatoreId ? idoneita.get(r.subappaltatoreId) : null;
        return (
          <div key={r.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 8, marginBottom: 6, background: "#fff" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <select aria-label="Ditta" value={val} disabled={disabled} style={{ ...inp, flex: "2 1 180px" }}
                onChange={e => {
                  const v = e.target.value;
                  if (v === "__libero") aggiorna(r.id, { subappaltatoreId: "", nome: "", libero: true });
                  else if (!v) aggiorna(r.id, { subappaltatoreId: "", nome: "", libero: false });
                  else { const o = opzioni.find(x => x.v === v); aggiorna(r.id, { subappaltatoreId: o?.subappaltatoreId || "", nome: o?.nome || "", libero: false }); }
                }}>
                <option value="">— Scegli la ditta —</option>
                {/* ditta salvata ma non nell'elenco (non più attiva, o anagrafica non leggibile da chi guarda) */}
                {val && val !== "__libero" && !opzioni.some(o => o.v === val) && <option value={val}>{r.nome || "Ditta salvata"}</option>}
                {opzioni.some(o => o.gruppo === "commessa") && <optgroup label="Ditte di questa commessa">
                  {opzioni.filter(o => o.gruppo === "commessa").map(o => <option key={o.v} value={o.v}>{o.nome}</option>)}
                </optgroup>}
                {opzioni.some(o => o.gruppo === "anagrafica") && <optgroup label="Altre ditte esterne">
                  {opzioni.filter(o => o.gruppo === "anagrafica").map(o => <option key={o.v} value={o.v}>{o.nome}</option>)}
                </optgroup>}
                <option value="__libero">Altra impresa (scrivi il nome)</option>
              </select>
              <input aria-label="Uomini presenti" type="text" inputMode="numeric" value={r.uomini ?? ""} disabled={disabled} placeholder="Uomini"
                onChange={e => aggiorna(r.id, { uomini: e.target.value.replace(/[^\d,.]/g, "") })} style={{ ...inp, flex: "0 0 86px" }} />
              {!disabled && <button type="button" onClick={() => onChange(righe.filter(x => x.id !== r.id))} aria-label="Togli la ditta" title="Togli"
                style={{ border: "none", background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: 15 }}>✕</button>}
            </div>
            {val === "__libero" && (
              <input aria-label="Nome dell'impresa" value={r.nome || ""} disabled={disabled} placeholder="Ragione sociale dell'impresa"
                onChange={e => aggiorna(r.id, { nome: e.target.value, subappaltatoreId: "", libero: true })}
                // il collegamento all'anagrafica (stesso nome) si fa uscendo dal campo, non a ogni
                // lettera: altrimenti «Edil» diventerebbe subito la ditta «Edil» e non si potrebbe
                // finire di scrivere «Edil Sud Srl» (W415)
                onBlur={e => {
                  const gia = anagrafica.find(a => nomeNorm(a.ragioneSociale) === nomeNorm(e.target.value));
                  if (gia) aggiorna(r.id, { subappaltatoreId: gia.id, nome: gia.ragioneSociale, libero: false });
                }} style={{ ...inp, marginTop: 6 }} />
            )}
            <input aria-label="Cosa hanno fatto" value={r.lavorazioni || ""} disabled={disabled} placeholder="Cosa hanno fatto oggi (facoltativo)"
              onChange={e => aggiorna(r.id, { lavorazioni: e.target.value })} style={{ ...inp, marginTop: 6, fontSize: 12.5 }} />
            {info && <div style={{ marginTop: 6 }}><BadgeIdoneita info={info} /></div>}
          </div>
        );
      })}
      {!disabled && (
        <button type="button" onClick={() => onChange([...righe, nuovaRiga()])}
          style={{ border: "1px dashed #c4b5fd", background: "#faf5ff", color: "#7c3aed", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
          + Aggiungi ditta presente
        </button>
      )}
      {righe.length > 0 && <span style={{ fontSize: 11.5, color: "#6b7280", marginLeft: 10 }}>Totale: {String(totale).replace(".", ",")} uomin{totale === 1 ? "o" : "i"}</span>}
      {String(testoVecchio || "").trim() && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 3 }}>Note scritte prima (testo libero, non entra negli uomini-giorno)</div>
          <textarea rows={2} value={testoVecchio} disabled={disabled} onChange={e => onTestoVecchio?.(e.target.value)} style={{ ...inp, resize: "vertical" }} />
        </div>
      )}
    </div>
  );
}
